import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiGet } from "../api/client";
import TownMultiSelect from "../components/TownMultiSelect";

interface AgentStats {
  agent_key: string;
  agent_name: string | null;
  brokerage_name: string | null;
  agent_phone: string | null;
  agent_email: string | null;
  active_count: number;
  sold_count: number;
  total_count: number;
  towns: string[];
  avg_days_on_market: number | null;
}

export default function Agents() {
  const [towns, setTowns] = useState<string[]>([]);

  const townsQ = useQuery<{ listings: { town: string }[] }>({
    queryKey: ["listings-towns"],
    queryFn: () => apiGet("/api/listings?status=all"),
  });
  const availableTowns = useMemo(() => {
    if (!townsQ.data) return [];
    const set = new Set<string>();
    for (const l of townsQ.data.listings) {
      if (l.town) set.add(l.town);
    }
    return Array.from(set).sort();
  }, [townsQ.data]);

  const townsParam = towns.length > 0 ? `?towns=${towns.map(encodeURIComponent).join(",")}` : "";

  const agentsQ = useQuery<{ count: number; agents: AgentStats[] }>({
    queryKey: ["agents", towns],
    queryFn: () => apiGet(`/api/agents${townsParam}`),
  });
  const agentList = agentsQ.data?.agents ?? [];

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0 }}>Agents</h1>
      </div>

      <div
        className="panel"
        style={{ marginBottom: 12, padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}
      >
        <FilterField label="Town">
          <TownMultiSelect towns={availableTowns} selected={towns} onChange={setTowns} />
        </FilterField>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Listing agents ({agentList.length})</h3>
        {agentsQ.isLoading && <p className="muted">Loading…</p>}
        {agentsQ.error && <p className="tag-error">Failed to load agents.</p>}
        {!agentsQ.isLoading && agentList.length === 0 && (
          <p className="muted">No agent data yet — this fills in as listings get re-scraped with detail pages.</p>
        )}
        {agentList.length > 0 && <AgentsTable agents={agentList} />}
      </div>
    </>
  );
}

type SortKey = "agent_name" | "total_count" | "active_count" | "avg_days_on_market";

function AgentsTable({ agents }: { agents: AgentStats[] }) {
  const [sortKey, setSortKey] = useState<SortKey>("total_count");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const sorted = useMemo(() => {
    const copy = [...agents];
    copy.sort((a, b) => {
      if (sortKey === "agent_name") {
        const av = a.agent_name ?? "";
        const bv = b.agent_name ?? "";
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [agents, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  return (
    <table>
      <thead>
        <tr>
          <SortableHeader label="Agent" sortKey="agent_name" active={sortKey} dir={sortDir} onClick={toggleSort} />
          <th>Brokerage</th>
          <th>Towns</th>
          <SortableHeader label="Total listings" sortKey="total_count" active={sortKey} dir={sortDir} onClick={toggleSort} />
          <SortableHeader label="Active now" sortKey="active_count" active={sortKey} dir={sortDir} onClick={toggleSort} />
          <SortableHeader
            label="Avg days on market"
            sortKey="avg_days_on_market"
            active={sortKey}
            dir={sortDir}
            onClick={toggleSort}
          />
          <th>Contact</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((a) => (
          <tr key={a.agent_key}>
            <td>{a.agent_name ?? "—"}</td>
            <td className="muted">{a.brokerage_name ?? "—"}</td>
            <td className="muted">{a.towns.join(", ") || "—"}</td>
            <td>{a.total_count}</td>
            <td className="muted">{a.active_count}</td>
            <td className="muted">{a.avg_days_on_market != null ? Math.round(a.avg_days_on_market) : "—"}</td>
            <td>
              <ContactLinks phone={a.agent_phone} email={a.agent_email} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ContactLinks({ phone, email }: { phone: string | null; email: string | null }) {
  if (!phone && !email) return <span className="muted">—</span>;
  return (
    <span style={{ display: "flex", gap: 8 }}>
      {phone && <a href={`tel:${phone}`}>Call</a>}
      {email && <a href={`mailto:${email}`}>Email</a>}
    </span>
  );
}

function SortableHeader({
  label,
  sortKey,
  active,
  dir,
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: "asc" | "desc";
  onClick: (key: SortKey) => void;
}) {
  const isActive = active === sortKey;
  return (
    <th onClick={() => onClick(sortKey)} style={{ cursor: "pointer", userSelect: "none" }}>
      {label}
      {isActive ? (dir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span className="muted" style={{ fontSize: 11 }}>
        {label}
      </span>
      {children}
    </label>
  );
}
